import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

import { useContent } from '@/content/ContentContext';
import { Chapter, ChapterColumn } from '@/features/journey/Chapter';
import { MOTION_ALLOWED } from '@/motion/queries';
import { Signature } from '@/ui/Brand/Signature';
import { cx } from '@/ui/classNames';
import { Container } from '@/ui/Layout/Container';
import { RevealText } from '@/ui/Reveal/RevealText';
import { typography } from '@/ui/Typography/typography';

import styles from './Manifesto.module.css';

/**
 * Chapitre 02, le studio, face au trou noir « Halo » : qui nous sommes en une
 * phrase, dont les mots s'éclairent au défilement, puis trois engagements.
 */
export function Manifesto() {
  const { manifesto } = useContent();
  const sectionRef = useRef<HTMLElement>(null);
  const leadRef = useRef<HTMLParagraphElement>(null);

  useGSAP(
    () => {
      const lead = leadRef.current;
      if (!lead) return;
      const media = gsap.matchMedia();
      media.add(MOTION_ALLOWED, () => {
        const split = SplitText.create(lead, { type: 'words' });
        gsap.fromTo(
          split.words,
          { opacity: 0.25 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.1,
            scrollTrigger: { trigger: lead, start: 'top 80%', end: 'bottom 55%', scrub: true },
          },
        );
        return () => {
          split.revert();
        };
      });
      return () => {
        media.revert();
      };
    },
    { scope: sectionRef },
  );

  return (
    <Chapter ref={sectionRef} chapter="studio" id="studio" aria-labelledby="studio-title">
      <Container>
        <ChapterColumn chapter="studio" className={styles.column}>
          <RevealText id="studio-title" className={cx(typography.heading, typography.h2)}>
            {manifesto.title} <span className={typography.accent}>{manifesto.titleAccent}</span>
          </RevealText>
          <p ref={leadRef} className={typography.lead}>
            {manifesto.lead}
          </p>
          <ol className={styles.pillars}>
            {manifesto.pillars.map((pillar, index) => (
              <li key={pillar.title} className={styles.pillar}>
                <span className={styles.pillarIndex} aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className={styles.pillarTitle}>{pillar.title}</span>
                <span className={styles.pillarText}>{pillar.text}</span>
              </li>
            ))}
          </ol>
          <Signature className={styles.signature} words={manifesto.signature} />
        </ChapterColumn>
      </Container>
    </Chapter>
  );
}
